import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { getDeviceFingerprint } from '@/utils/fingerprint';

type VerificationStatus = 'verifying' | 'success' | 'error' | 'expired';

/**
 * Magic Link Verify Page
 * Handles verification of magic link tokens and logs the user in
 * WP-AUTH-7: Supports MFA redirect when user has MFA enabled
 */
export default function MagicLinkVerifyPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { verifyMagicLink: storeVerifyMagicLink } = useAuthStore();

  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  // Device fingerprint for MFA device tracking
  const deviceFingerprintRef = useRef<string | null>(null);

  // Initialize device fingerprint on mount
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        deviceFingerprintRef.current = await getDeviceFingerprint();
      } catch {
        // Fingerprint collection is best-effort, don't block verification
      }
    };
    initFingerprint();
  }, []);

  const verifyMagicLink = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid magic link.');
      return;
    }

    try {
      // Use store method which handles MFA checking and state updates
      const result = await storeVerifyMagicLink(token, deviceFingerprintRef.current || undefined);

      // WP-AUTH-7: Handle MFA requirement
      if (result.requiresMfa && result.mfaToken) {
        navigate('/mfa', {
          state: {
            mfaToken: result.mfaToken,
            from: { pathname: '/dashboard' },
            deviceFingerprint: deviceFingerprintRef.current,
          },
          replace: true,
        });
        return;
      }

      // MFA not required - show success and redirect
      setStatus('success');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('expired')) {
          setStatus('expired');
          setErrorMessage('This magic link has expired. Please request a new one.');
        } else if (err.message.includes('already used')) {
          setStatus('error');
          setErrorMessage('This magic link has already been used. Please request a new one.');
        } else {
          setStatus('error');
          setErrorMessage(err.message);
        }
      } else {
        setStatus('error');
        setErrorMessage('Failed to verify magic link. Please try again.');
      }
    }
  }, [token, navigate, storeVerifyMagicLink]);

  useEffect(() => {
    verifyMagicLink();
  }, [verifyMagicLink]);

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Spinner size="lg" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Signing you in</h2>
        <p className="text-gray-600">Please wait while we verify your magic link...</p>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back!</h2>
        <p className="text-gray-600 mb-4">
          You have been signed in successfully.
        </p>
        <Alert variant="info">
          Redirecting to your dashboard...
        </Alert>
      </div>
    );
  }

  // Expired state
  if (status === 'expired') {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg
            className="h-6 w-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h2>
        <p className="text-gray-600 mb-6">{errorMessage}</p>
        <div className="space-y-3">
          <Link to="/magic-link">
            <Button className="w-full">Request new magic link</Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="w-full">Sign in with password</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
        <svg
          className="h-6 w-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign-in Failed</h2>
      <p className="text-gray-600 mb-6">{errorMessage}</p>
      <div className="space-y-3">
        <Link to="/magic-link">
          <Button className="w-full">Request new magic link</Button>
        </Link>
        <Link to="/login">
          <Button variant="outline" className="w-full">Sign in with password</Button>
        </Link>
      </div>
    </div>
  );
}
